import { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { generatePath, NavLink, useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarItem,
  Header,
  HeaderLogo,
  IconAppLogo,
  HeaderRightSidebar,
  Avatar,
  IconLoader,
  SidebarLink,
  PermissionEnum,
  apiAbortController,
  IconNotification,
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  ButtonSecondary,
  formatDate,
  IconFile,
  IconDownload,
  SidePanelLoader,
  ClickOutsideDetector,
  ToggleInput,
  IconInfo,
  NotifiactionHintPopup,
  IconAlternativeSettings,
  IconLogout,
  NotifiactionDisabledPopup,
  IconAnimatedDots,
  IconArrowRight,
  usePrevious,
  IconSelected,
} from '@ui/src';
import { NotificationGroupEnum } from '@ui/src/base-types';
import AccountsRoutes from '@accounts/routes';
import type { CurrentUser } from '@auth/hoc/types';
import ClientsRoutes from '@clients/routes';
import {
  BankRoutes,
  FeesRoutes,
  TariffsRoutes,
  CardsRoutes,
  CurrenciesRoutes,
} from '@configuration/routes';
import ExchangeRoutes from '@exchange/routes';
import DashboardRoutes from '@dashboard/routes';
import AdministrationRoutes from '@administration/routes';
import { useLogoutMutation } from '@administration/hoc/settings/profile/store/services';
import { setBrowserNotificationEnabled } from '@administration/hoc/settings/profile/store/slices';
import MessagesRoutes from '@messaging/routes';
import { setAccessToken as setMessagesAccessToken } from '@messaging/hoc/store/slices/general';
import { NotificationEntityType } from '@notifications/hoc/center/types';
import NotificationCenterRoutes from '@notifications/routes';
import ReportsRoutes from '@reports/routes';
import AmlKycRoutes from '@aml/routes';
import TransactionsRoutes from '@transactions/routes';
import { setAccessToken as setTransactionsAccessToken } from '@transactions/hoc/transactions/store/slice';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useGetRootMessageTokenMutation,
  useLazyGetNotificationChannelsQuery,
  useLazyGetNotificationChannelMessagesQuery,
  useReadAllNotificationsMutation,
  useUnreadAllNotificationsMutation,
  useLazyGetNotificationFileQuery,
  useLazyGetRootNotificationChannelEntityQuery,
  useLazyGetRootApplicationNotificationEntityQuery,
} from '../../store/service';
import ActiveTabCtxProvider, {
  ActiveTabContext,
} from './context/active-tab-context';
import {
  setFocused,
  setNotificationChannelId,
  setNotificationVisible,
} from '../../store/slice';
import { LayoutWrapperTypes, LayoutTypes } from './types';
import NetworkStatusBanner from './network-status-banner';

import styles from './layout.module.css';

const generateAvatar = (user: CurrentUser): React.JSX.Element => {
  let content;

  if (user.profile.avatarImg) {
    content = <Avatar imageUrl={user.profile.avatarImg} />;
  } else {
    content = (
      <div className={styles.layoutAvatar}>
        <div>{`${user.profile.firstName
          .charAt(0)
          .toUpperCase()}${user.profile.lastName
          .charAt(0)
          .toUpperCase()}`}</div>
      </div>
    );
  }

  return content;
};

const Layout = ({
  children,
  sendMessage,
  messagesIsLoading,
  reSubscribe,
  setNotificationPanelVisible,
  notificationPanelVisible,
  selectedNotificationId,
  setSelectedNotificationId,
}: LayoutTypes) => {
  const [cardsEnabled, setCardsEnabled] = useState(false);
  const [cashCollectionEnabledValue, setCashCollectionEnabled] =
    useState(false);
  const { focused } = useContext(ActiveTabContext);
  const prevFocused = usePrevious(focused);

  const [selectedFileId, setSelectedFileId] = useState('');
  const [onReadInProgress, setOnReadtInProgress] = useState(false);
  const [nextMessagesIsLoading, setNextMessagesIsLoading] = useState(false);
  const [profileMenuVisible, setProfileMenuVisible] = useState(false);
  const [notificationHintVisible, setNotificationHintVisible] = useState(false);
  const [browserNotificationEnabledValue, setBrowserNotificationEnabledValue] =
    useState(false);
  const [
    notificationDisabledPopupVisible,
    setNotificationDisabledPopupVisible,
  ] = useState(false);
  const [selectedNotificationItemId, setSelectedNotificationItemId] =
    useState('');

  const [readAllNotifications, { isLoading, isSuccess }] =
    useReadAllNotificationsMutation();
  const [readAllNotificationsById] = useReadAllNotificationsMutation();
  const [unreadAllNotificationsById] = useUnreadAllNotificationsMutation();
  const [getNotificationsMessages, { isFetching }] =
    useLazyGetNotificationChannelMessagesQuery();
  const [logout, { isLoading: logoutInProgress }] = useLogoutMutation();
  const [
    getRootNotificationChannelEntity,
    { isLoading: channelEntityIsLoading, isFetching: channelEntityIsFetching },
  ] = useLazyGetRootNotificationChannelEntityQuery();
  const [
    getRootApplicationNotificationEntity,
    {
      isLoading: applicationEntityIsLoading,
      isFetching: applicationEntityIsFetching,
    },
  ] = useLazyGetRootApplicationNotificationEntityQuery();
  const [getMessagesToken] = useGetRootMessageTokenMutation();

  const { user } = useAppSelector((state) => state.auth);
  const {
    activePermissions,
    logo,
    favicon,
    cashCollectionEnabled,
    cardsEnabledValue,
    notificationsMessages,
    notificationChannelId,
    notificationMessagesPagination,
    settings,
    userId,
    notificationVisible,
    notificationText,
  } = useAppSelector((state) => state.root);
  const { roles } = useAppSelector((state) => state.userRoles.roles);
  const { browserNotificationEnabled } = useAppSelector(
    (state) => state.settings.profile,
  );
  const { values: transactionsTypes } = useAppSelector(
    (state) => state.settings.transactionsTypes,
  );

  const { pathname } = useLocation();

  const listRef = useRef<HTMLDivElement | null>(null);

  const [
    getReportFile,
    { isLoading: downloadIsLoading, isFetching: downloadIsFetching },
  ] = useLazyGetNotificationFileQuery();

  let content;

  const cardsEnabledJSON = localStorage.getItem('cardsEnabled');

  const dispatch = useAppDispatch();

  const onChange = (checked: boolean) => {
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();

        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            const browserNotificationEnabledJSON = localStorage.getItem(
              'browserNotificationEnabled',
            );

            let nextBrowserNotificationEnabled: {
              browserNotificationEnabled: boolean;
              userId?: string;
            }[] = [];

            if (browserNotificationEnabledJSON) {
              const parsed = JSON.parse(browserNotificationEnabledJSON);

              if (parsed && Array.isArray(parsed)) {
                if (parsed?.find((item) => item.userId === userId)) {
                  nextBrowserNotificationEnabled = parsed?.map((item) => {
                    const result = { ...item };

                    if (item.userId === userId) {
                      result.browserNotificationEnabled = checked;
                    }

                    return result;
                  });
                } else {
                  nextBrowserNotificationEnabled.push({
                    browserNotificationEnabled: checked,
                    userId,
                  });
                }
              } else {
                nextBrowserNotificationEnabled = [
                  { browserNotificationEnabled: checked, userId },
                ];
              }
            } else {
              nextBrowserNotificationEnabled = [
                { browserNotificationEnabled: checked, userId },
              ];
            }

            localStorage.setItem(
              'browserNotificationEnabled',
              JSON.stringify(nextBrowserNotificationEnabled),
            );

            setBrowserNotificationEnabledValue(checked);
            dispatch(setBrowserNotificationEnabled(checked));
          }
        });
      } else if (Notification.permission === 'granted') {
        setBrowserNotificationEnabledValue(checked);
        dispatch(setBrowserNotificationEnabled(checked));

        const browserNotificationEnabledJSON = localStorage.getItem(
          'browserNotificationEnabled',
        );

        let nextBrowserNotificationEnabled: {
          browserNotificationEnabled: boolean;
          userId?: string;
        }[] = [];

        if (browserNotificationEnabledJSON) {
          const parsed = JSON.parse(browserNotificationEnabledJSON);

          if (parsed && Array.isArray(parsed)) {
            if (parsed?.find((item) => item.userId === userId)) {
              nextBrowserNotificationEnabled = parsed?.map((item) => {
                const result = { ...item };

                if (item.userId === userId) {
                  result.browserNotificationEnabled = checked;
                }

                return result;
              });
            } else {
              nextBrowserNotificationEnabled.push({
                browserNotificationEnabled: checked,
                userId,
              });
            }
          } else {
            nextBrowserNotificationEnabled = [
              { browserNotificationEnabled: checked, userId },
            ];
          }
        } else {
          nextBrowserNotificationEnabled = [
            { browserNotificationEnabled: checked, userId },
          ];
        }

        localStorage.setItem(
          'browserNotificationEnabled',
          JSON.stringify(nextBrowserNotificationEnabled),
        );
      } else if (Notification.permission === 'denied') {
        setNotificationDisabledPopupVisible(true);
      }
    }
  };

  const onLogout = async () => {
    const response = await logout();

    if (response) {
      localStorage.removeItem('activePermissions');
      localStorage.removeItem('userId');
      localStorage.removeItem('cardsEnabled');
      localStorage.removeItem('reportLink');
      localStorage.removeItem('responseTime');

      window.location.href = '/login';
    }
  };

  const onReadById = async (id: string) => {
    readAllNotificationsById({
      id: notificationChannelId as string,
      body: {
        messages: [id],
        channelId: notificationChannelId as string,
      },
    });
  };

  const onUnreadById = async (id: string) => {
    unreadAllNotificationsById({
      id: notificationChannelId as string,
      body: {
        messages: [id],
        channelId: notificationChannelId as string,
      },
    });
  };

  const onReadAll = async () => {
    readAllNotifications({
      id: notificationChannelId as string,
      body: {
        messages: notificationsMessages?.map((item) => item.id) as string[],
        channelId: notificationChannelId as string,
      },
    });
  };

  const onNotificationMessagesScroll = async (scroll: number) => {
    if (
      listRef &&
      listRef.current &&
      notificationMessagesPagination?.hasNextPage
    ) {
      if (
        scroll >
        listRef.current.scrollHeight - listRef.current.clientHeight - 200
      ) {
        setNextMessagesIsLoading(true);

        if (!nextMessagesIsLoading) {
          const response = await getNotificationsMessages({
            id: notificationChannelId as string,
            cursor: notificationMessagesPagination.endCursor,
          });

          if (response) {
            setNextMessagesIsLoading(false);
          }
        }
      }
    }
  };

  const getRootApplicationNotificationEntityAction = async (
    id: string,
    notificationId: string,
  ) => {
    setSelectedNotificationItemId(notificationId);

    const response = await getRootApplicationNotificationEntity(id);

    if (response) {
      let href = '/customers/new-application';

      if (response?.data?.data?.type === 'CORPORATE') {
        href = `${href}/corporate/${id}`;
      } else if (response?.data?.data?.type === 'INDIVIDUAL') {
        href = `${href}/individual/${id}`;
      } else if (
        response?.data?.data?.type === 'COMPANY_OFFICIAL' ||
        response?.data?.data?.type === 'MAIN_REPRESENTATIVE'
      ) {
        href = `${href}/company-official/${id}`;
      }

      const hiddenElement = document.createElement('a');
      hiddenElement.href = href;
      hiddenElement.target = '_blank';
      hiddenElement.click();

      setSelectedNotificationItemId('');
    }
  };

  const getRootNotificationChannelEntityAction = async (
    id: string,
    notificationId: string,
  ) => {
    setSelectedNotificationItemId(notificationId);

    const response = await getRootNotificationChannelEntity(id);

    if (response) {
      let href = '/messages';

      if (response?.data?.data?.channelType === 'MAIN') {
        href = `${href}/main-chat/all/${id}`;
      } else {
        href = `${href}/requests/all/${id}`;
      }

      const hiddenElement = document.createElement('a');
      hiddenElement.href = href;
      hiddenElement.target = '_blank';
      hiddenElement.click();

      setSelectedNotificationItemId('');
    }
  };

  const onDownload = async (
    fileId: string,
    name: string,
    messageId: string,
  ) => {
    setSelectedFileId(fileId);

    const response = await getReportFile(fileId);

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = name;
      hiddenElement.click();

      await readAllNotifications({
        id: notificationChannelId as string,
        body: {
          messages: [messageId],
          channelId: notificationChannelId as string,
        },
      });
    }
  };

  useEffect(() => {
    dispatch(setFocused(focused));
  }, [focused, dispatch]);

  useEffect(() => {
    if (cardsEnabledJSON) {
      const parsed = JSON.parse(cardsEnabledJSON);

      setCardsEnabled(parsed.cardsEnabled);
    } else {
      setCardsEnabled(cardsEnabledValue as boolean);
    }
  }, [cardsEnabledJSON, cardsEnabledValue]);

  useEffect(() => {
    setCashCollectionEnabled(cashCollectionEnabled as boolean);
  }, [cashCollectionEnabled]);

  useEffect(() => {
    if (favicon) {
      const faviconElement = document.getElementById('favicon');

      if (faviconElement) {
        // @ts-ignore
        faviconElement.href = favicon;
      }
    }
  }, [favicon]);

  useEffect(() => {
    if (isSuccess) {
      setOnReadtInProgress(false);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (
      notificationChannelId &&
      (activePermissions?.includes(PermissionEnum.MESSAGES_LIST) ||
        activePermissions?.includes(PermissionEnum.CHANNELS_LIST) ||
        activePermissions?.includes(PermissionEnum.CHANNELS_CREATE_ACTION) ||
        activePermissions?.includes(PermissionEnum.CHANNELS_UPDATE_ACTION))
    ) {
      if (!notificationsMessages) {
        getNotificationsMessages({ id: notificationChannelId });
      }

      if (reSubscribe) {
        getNotificationsMessages({ id: notificationChannelId, refresh: true });

        sendMessage(
          JSON.stringify({
            event: 'subscribe',
            data: { channelId: notificationChannelId },
          }),
        );
      }

      if (focused && prevFocused !== null && !prevFocused) {
        getNotificationsMessages({ id: notificationChannelId, refresh: true });
        getMessagesToken();
      }
    }
  }, [
    notificationChannelId,
    reSubscribe,
    prevFocused,
    focused,
    notificationsMessages,
    activePermissions,
    getNotificationsMessages,
    sendMessage,
  ]);

  useEffect(() => {
    if (notificationPanelVisible && selectedNotificationId) {
      setTimeout(() => {
        setSelectedNotificationId('');
      }, 3000);
    }
  }, [
    notificationPanelVisible,
    selectedNotificationId,
    setSelectedNotificationId,
  ]);

  useEffect(() => {
    setBrowserNotificationEnabledValue(browserNotificationEnabled as boolean);
  }, [browserNotificationEnabled]);

  const removeStorageFilters = () => {
    localStorage.removeItem('accountsListFilters');
    localStorage.removeItem('individualOrganizationsFilters');
    localStorage.removeItem('corporateOrganizationsFilters');
    localStorage.removeItem('banksFilters');
    localStorage.removeItem('tariffsFilters');
    localStorage.removeItem('reportLink');
    localStorage.removeItem('responseTime');
    localStorage.removeItem('logsResponseTime');
  };

  const macOS = [/Mac OS/i];

  const isMac = macOS.some((toMatchItem) =>
    navigator.userAgent.match(toMatchItem),
  );

  let notificationMessagesContent = <div />;

  if ((messagesIsLoading && notificationChannelId) || !notificationChannelId) {
    notificationMessagesContent = <SidePanelLoader />;
  } else if (!messagesIsLoading && !notificationsMessages?.length) {
    notificationMessagesContent = (
      <div className={styles.noNotifications}>No notifications</div>
    );
  } else if (!messagesIsLoading && notificationsMessages?.length) {
    notificationMessagesContent = (
      <div
        className={styles.messagesList}
        ref={listRef}
        onScroll={(e) =>
          onNotificationMessagesScroll(
            (e.target as unknown as { scrollTop: number }).scrollTop,
          )
        }
      >
        {notificationsMessages?.map((item) => {
          let messageContent: any;
          let title;
          let notificationTitle = <div />;
          let notificationCenterLink = '';
          let notificationCenterLinkSection: string =
            NotificationCenterRoutes.NOTIFICATIONS_CENTER;

          let notificationEntityLink = '';
          let linkName = '';

          if (item.content) {
            if (typeof item.content === 'string') {
              messageContent = JSON.parse(item.content);
            } else {
              messageContent = item.content;
            }
          }

          if (messageContent) {
            if (item.eventType === 'NOTIFICATION') {
              const titleArray = messageContent.notification.type
                ?.split('_')
                .map(
                  (type: string) =>
                    `${type.charAt(0)}${type.slice(1).toLowerCase()}`,
                );

              notificationTitle = (
                <div>
                  <strong>{titleArray[0]?.replace('Issues', 'Tasks')}</strong>{' '}
                  -&nbsp;
                  {titleArray.slice(1).join(' ')}
                </div>
              );

              if (
                (messageContent?.notification?.entityType ===
                  NotificationEntityType.APPLICATION ||
                  messageContent?.entityType ===
                    NotificationEntityType.OFFICIALS ||
                  messageContent?.entityType ===
                    NotificationEntityType.WARNING_GROUP ||
                  messageContent.entityType ===
                    NotificationEntityType.ORGANIZATION ||
                  messageContent.entityType ===
                    NotificationEntityType.CLIENT_SCORE_RESULT ||
                  messageContent.entityType ===
                    NotificationEntityType.AGREEMENT ||
                  messageContent.entityType ===
                    NotificationEntityType.DOCUMENT ||
                  messageContent?.entityType === NotificationEntityType.LOCK) &&
                activePermissions?.includes(
                  PermissionEnum.NOTIFICATIONS_MESSAGING_AND_CUSTOMERS_LIST,
                )
              ) {
                notificationCenterLinkSection =
                  ClientsRoutes.CUSTOMERS_NOTIFICATIONS_CENTER;
              } else if (
                (messageContent?.notification?.entityType ===
                  NotificationEntityType.TRANSACTION ||
                  messageContent?.notification?.entityType ===
                    NotificationEntityType.PAYMENT_ORDER) &&
                activePermissions?.includes(
                  PermissionEnum.NOTIFICATIONS_TRANSACTIONS_LIST,
                )
              ) {
                notificationCenterLinkSection =
                  TransactionsRoutes.TRANSACTIONS_NOTIFICATIONS_CENTER;
              }

              notificationCenterLink = `${notificationCenterLinkSection}?id=${messageContent?.notification?.id}&category=${messageContent?.notification?.category}&group=${messageContent?.notification?.group}&type=${messageContent?.notification?.type}`;

              if (
                messageContent?.notification?.entityType ===
                NotificationEntityType.APPLICATION
              ) {
                linkName = 'Application';
                notificationEntityLink = '/customers/new-application';
              } else if (
                messageContent?.notification?.entityType ===
                NotificationEntityType.CHANNEL
              ) {
                linkName = 'Chat';
                notificationEntityLink = '/messages';
              } else if (
                (messageContent?.notification?.entityType ===
                  NotificationEntityType.OFFICIALS ||
                  messageContent?.notification?.entityType ===
                    NotificationEntityType.WARNING_GROUP ||
                  messageContent?.notification.entityType ===
                    NotificationEntityType.ORGANIZATION ||
                  messageContent?.notification.entityType ===
                    NotificationEntityType.CLIENT_SCORE_RESULT ||
                  messageContent?.notification.entityType ===
                    NotificationEntityType.AGREEMENT ||
                  messageContent?.notification.entityType ===
                    NotificationEntityType.DOCUMENT) &&
                messageContent?.notification?.organizationId
              ) {
                linkName = 'Organization';
                notificationEntityLink = '/customers';

                if (
                  messageContent?.notification?.group ===
                  NotificationGroupEnum.CUSTOMERS_CORPORATE
                ) {
                  notificationEntityLink = '/customers/corporate';
                } else if (
                  messageContent?.notification?.group ===
                  NotificationGroupEnum.CUSTOMERS_INDIVIDUAL
                ) {
                  notificationEntityLink = '/customers/individual';
                }

                notificationEntityLink = `${notificationEntityLink}/${messageContent?.notification?.organizationId}`;

                if (
                  messageContent?.notification?.entityType ===
                  NotificationEntityType.OFFICIALS
                ) {
                  notificationEntityLink = `${notificationEntityLink}/overview/transactions/company-official`;
                }

                if (
                  messageContent?.notification?.entityType ===
                    NotificationEntityType.WARNING_GROUP ||
                  messageContent?.notification.entityType ===
                    NotificationEntityType.CLIENT_SCORE_RESULT
                ) {
                  notificationEntityLink = `${notificationEntityLink}/aml/transactions`;
                } else if (
                  messageContent?.notification.entityType ===
                    NotificationEntityType.AGREEMENT ||
                  messageContent?.notification.entityType ===
                    NotificationEntityType.DOCUMENT
                ) {
                  notificationEntityLink = `${notificationEntityLink}/documents`;
                }
              } else if (
                messageContent?.notification?.entityType ===
                NotificationEntityType.TRANSACTION
              ) {
                notificationEntityLink = '/transactions';
                linkName = 'Transaction';

                if (
                  messageContent?.notification?.group ===
                  NotificationGroupEnum.TRANSACTIONS_EXCHANGE
                ) {
                  notificationEntityLink = `${notificationEntityLink}/exchange`;
                } else if (
                  messageContent?.notification?.group ===
                  NotificationGroupEnum.TRANSACTIONS_OUTGOING
                ) {
                  notificationEntityLink = `${notificationEntityLink}/outgoing`;
                } else if (
                  messageContent?.notification?.group ===
                  NotificationGroupEnum.TRANSACTIONS_INCOMING
                ) {
                  notificationEntityLink = `${notificationEntityLink}/incoming`;
                } else if (
                  messageContent?.notification?.group ===
                  NotificationGroupEnum.TRANSACTIONS_NOSTRO
                ) {
                  notificationEntityLink = `${notificationEntityLink}/nostro`;
                } else if (
                  messageContent?.notification?.group ===
                  NotificationGroupEnum.TRANSACTIONS_INTERNAL
                ) {
                  notificationEntityLink = `${notificationEntityLink}/internal`;
                }

                notificationEntityLink = `${notificationEntityLink}/${messageContent?.notification?.entityId}`;
              } else if (
                messageContent?.notification?.entityType ===
                NotificationEntityType.PAYMENT_ORDER
              ) {
                notificationEntityLink = `/transactions/cash-collection/list/${messageContent?.notification?.entityId}`;
                linkName = 'Cash Collection';
              } else if (
                messageContent?.notification?.entityType ===
                NotificationEntityType.NOTIFICATION
              ) {
                notificationEntityLink = `${NotificationCenterRoutes.NOTIFICATIONS_CENTER}?id=${messageContent?.notification?.entityId}`;
                linkName = 'Notification';
              } else if (
                messageContent?.notification?.entityType ===
                NotificationEntityType.LOCK
              ) {
                notificationEntityLink = `${AdministrationRoutes.SECURITY_LOCKS}/${messageContent?.notification?.entityId}`;
                linkName = 'Lock';
              }
            }

            if (item.eventType === 'ON_FILE_READY') {
              const formattedName = messageContent.name.replace('.xlsx', '');
              const titleArray = formattedName.split('-');

              title = titleArray
                ?.map(
                  (titleItem: string) =>
                    `${titleItem?.charAt(0)?.toUpperCase()}${titleItem
                      ?.slice(1)
                      ?.toLowerCase()}`,
                )
                .join(' ');
            } else if (item.eventType === 'ON_ACCOUNT_CREATE') {
              title = `Account ${messageContent.fullNumber} ${
                messageContent.clientName
                  ? `for ${messageContent.clientName}`
                  : ''
              } was created`;
            } else if (item.eventType === 'ON_PSP_ACCOUNT_CREATE') {
              title = `Bank account ${messageContent.companyName ? `in ${messageContent.companyName}` : ''} was created`;
            } else {
              title = messageContent.name;
            }
          }

          let exportTitle = '';

          if (item.eventType === 'ON_FILE_READY') {
            exportTitle = 'Report ready: ';

            if (title.toLowerCase().includes('transaction')) {
              exportTitle = 'Export ready: ';
            }
          }

          return (
            <div
              className={`${styles.notification} ${
                selectedNotificationId === item.id ? styles.blink : ''
              }`}
              id={item.id}
              key={item.id}
            >
              <button
                type="button"
                className={styles.ureadHint}
                aria-label="Read notification"
                onClick={() =>
                  !item.readAt
                    ? onReadById(item.id as string)
                    : onUnreadById(item.id as string)
                }
                data-text={!item.readAt ? 'Mark as read' : 'Mark as unread'}
              >
                {!item.readAt && <div className={styles.ureadHintCircle} />}
              </button>

              <div className={styles.notificationContent}>
                <div className={styles.notificationTop}>
                  <div className={styles.notificationTitle}>
                    {item.eventType === 'NOTIFICATION' && notificationTitle}
                    {item.eventType !== 'NOTIFICATION' && (
                      <div>
                        <span>{exportTitle}</span>
                        {title}
                      </div>
                    )}
                  </div>
                </div>
                {item.eventType === 'NOTIFICATION' &&
                  messageContent.notification?.organization && (
                    <div className={styles.notificationOrganization}>
                      {`Customer: ${
                        messageContent.notification?.organization?.firstName
                          ? `${messageContent.notification?.organization?.firstName} ${messageContent.notification?.organization?.lastName}`
                          : messageContent.notification?.organization
                              ?.companyName
                      }`}
                    </div>
                  )}
                {item.eventType === 'NOTIFICATION' && (
                  <div className={styles.accountsLinks}>
                    <div className={styles.accountsLinkLeft}>
                      {notificationEntityLink && (
                        <NavLink
                          to={notificationEntityLink}
                          target="_blank"
                          className={styles.accountLink}
                          onClick={(e) => {
                            if (!item.readAt) {
                              onReadById(item.id as string);
                            }

                            if (
                              messageContent?.notification?.entityType ===
                              NotificationEntityType.CHANNEL
                            ) {
                              e.preventDefault();
                              getRootNotificationChannelEntityAction(
                                messageContent?.notification?.entityId,
                                messageContent?.notification?.id,
                              );
                            }

                            if (
                              messageContent?.notification?.entityType ===
                              NotificationEntityType.APPLICATION
                            ) {
                              e.preventDefault();
                              getRootApplicationNotificationEntityAction(
                                messageContent?.notification?.entityId,
                                messageContent?.notification?.id,
                              );
                            }
                          }}
                        >
                          {messageContent?.notification?.entityType ===
                            NotificationEntityType.CHANNEL &&
                            (selectedNotificationItemId ===
                              messageContent?.notification?.id &&
                            (channelEntityIsLoading ||
                              channelEntityIsFetching) ? (
                              <IconAnimatedDots
                                className={styles.chatEntityLoader}
                              />
                            ) : (
                              linkName
                            ))}
                          {messageContent?.notification?.entityType ===
                            NotificationEntityType.APPLICATION &&
                            (selectedNotificationItemId ===
                              messageContent?.notification?.id &&
                            (applicationEntityIsLoading ||
                              applicationEntityIsFetching) ? (
                              <IconAnimatedDots
                                className={styles.chatEntityLoader}
                              />
                            ) : (
                              linkName
                            ))}
                          {messageContent?.notification?.entityType !==
                            NotificationEntityType.CHANNEL &&
                            messageContent?.notification?.entityType !==
                              NotificationEntityType.APPLICATION &&
                            linkName}
                          <IconArrowRight width={13} height={13} />
                        </NavLink>
                      )}
                      {messageContent?.notification?.entityType ===
                        NotificationEntityType.LOCK &&
                        messageContent?.notification?.organizationId && (
                          <NavLink
                            to={generatePath(
                              ClientsRoutes.CUSTOMERS_INDIVIDUAL_OVERVIEW,
                              {
                                customerId:
                                  messageContent?.notification?.organizationId,
                              },
                            )}
                            target="_blank"
                            className={styles.accountLink}
                            onClick={() => {
                              if (!item.readAt) {
                                onReadById(item.id as string);
                              }
                            }}
                          >
                            Organization
                            <IconArrowRight width={13} height={13} />
                          </NavLink>
                        )}
                      {notificationCenterLink &&
                        messageContent?.notification?.entityType !==
                          NotificationEntityType.ISSUE &&
                        messageContent?.notification?.entityType !==
                          NotificationEntityType.NOTIFICATION && (
                          <NavLink
                            to={notificationCenterLink}
                            target="_blank"
                            className={styles.accountLink}
                            onClick={() => {
                              if (!item.readAt) {
                                onReadById(item.id as string);
                              }
                            }}
                          >
                            Notification
                            <IconArrowRight width={13} height={13} />
                          </NavLink>
                        )}
                    </div>

                    <div className={styles.notificationDate}>
                      {formatDate(item.createdAt, true)}
                    </div>
                  </div>
                )}
                {item.eventType === 'ON_ACCOUNT_CREATE' && messageContent && (
                  <div className={styles.accountsLinks}>
                    <div className={styles.accountsLinkLeft}>
                      {messageContent.organizationId && (
                        <NavLink
                          to={`/customers/${
                            messageContent.organizationType === 'CORPORATE'
                              ? 'corporate'
                              : 'individual'
                          }/${
                            messageContent.organizationId
                          }/accounts/transactions`}
                          target="_blank"
                          className={styles.accountLink}
                          onClick={() => {
                            if (!item.readAt) {
                              onReadById(item.id as string);
                            }
                          }}
                        >
                          Organization
                          <IconArrowRight width={13} height={13} />
                        </NavLink>
                      )}

                      <NavLink
                        to={`/accounts/list/${messageContent.accountId}?categoryId=${messageContent.categoryId}&groupId=${messageContent.groupId}&primaryId=${messageContent.primaryId}&sortProp=fullNumber&sortDir=asc`}
                        target="_blank"
                        className={styles.accountLink}
                        onClick={() => {
                          if (!item.readAt) {
                            onReadById(item.id as string);
                          }
                        }}
                      >
                        Accounts
                        <IconArrowRight width={13} height={13} />
                      </NavLink>
                    </div>

                    <div className={styles.notificationDate}>
                      {formatDate(item.createdAt, true)}
                    </div>
                  </div>
                )}
                {item.eventType === 'ON_PSP_ACCOUNT_CREATE' &&
                  messageContent && (
                    <div className={styles.accountsLinks}>
                      <div className={styles.accountsLinkLeft}>
                        <NavLink
                          to={`/bank-and-psp/accounts/${messageContent.id}`}
                          target="_blank"
                          className={styles.accountLink}
                          onClick={() => {
                            if (!item.readAt) {
                              onReadById(item.id as string);
                            }
                          }}
                        >
                          Bank Account
                          <IconArrowRight width={13} height={13} />
                        </NavLink>

                        <NavLink
                          to={`/bank-and-psp/${messageContent.pspPartnerId}`}
                          target="_blank"
                          className={styles.accountLink}
                          onClick={() => {
                            if (!item.readAt) {
                              onReadById(item.id as string);
                            }
                          }}
                        >
                          Bank
                          <IconArrowRight width={13} height={13} />
                        </NavLink>
                      </div>

                      <div className={styles.notificationDate}>
                        {formatDate(item.createdAt, true)}
                      </div>
                    </div>
                  )}
                {item.eventType === 'ON_FILE_READY' && messageContent && (
                  <div className={styles.notificationFileWrapper}>
                    <div className={styles.notificationFile}>
                      <div className={styles.notificationFileName}>
                        <IconFile width={20} height={20} />
                        <span>{messageContent?.name}</span>
                      </div>
                      <button
                        type="button"
                        className={styles.notificationDownloadFileButton}
                        aria-label="Download file"
                        onClick={() => {
                          onDownload(
                            messageContent.uploadId,
                            messageContent.name,
                            item.id as string,
                          );

                          if (!item.readAt) {
                            onReadById(item.id as string);
                          }
                        }}
                      >
                        {(downloadIsLoading || downloadIsFetching) &&
                        selectedFileId === messageContent.uploadId ? (
                          <IconLoader width={18} height={18} />
                        ) : (
                          <IconDownload width={18} height={18} />
                        )}
                      </button>
                    </div>
                    <div className={styles.notificationDate}>
                      {formatDate(item.createdAt, true)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {nextMessagesIsLoading && (
          <div className={styles.nextMessagesLoader}>
            <IconLoader />
          </div>
        )}
      </div>
    );
  }

  if (user && activePermissions) {
    content = (
      <>
        <div className={styles.layoutHeader}>
          <Header>
            <HeaderLogo>
              {logo ? (
                <img src={logo} alt="Logo" className={styles.headerLogo} />
              ) : (
                <IconAppLogo />
              )}
            </HeaderLogo>

            <HeaderRightSidebar>
              <>
                <ClickOutsideDetector
                  setOptionsListVisible={setProfileMenuVisible}
                >
                  <div className={styles.profileMenuWrapper}>
                    <button
                      type="button"
                      className={styles.profileLink}
                      onClick={() => setProfileMenuVisible(!profileMenuVisible)}
                    >
                      <span>{`${user?.profile.firstName} ${user?.profile.lastName}`}</span>
                      {generateAvatar(user)}
                    </button>
                    {profileMenuVisible && (
                      <div className={styles.profileMenu}>
                        <div className={styles.profileMenuRole}>
                          {
                            roles?.find(
                              (item) =>
                                item.id === user?.employeeRoleTemplateId,
                            )?.name
                          }
                        </div>
                        <div
                          className={styles.profileMenuId}
                        >{`ID: ${user.id}`}</div>
                        <div className={styles.profileMenuNotificationToggle}>
                          <ToggleInput
                            name="browserNotification"
                            title="Browser notifications"
                            visualClassName={styles.browserNotificationVisual}
                            checked={browserNotificationEnabledValue}
                            onChange={(e) => onChange(e.target.checked)}
                          />
                        </div>
                        {isMac && (
                          <button
                            type="button"
                            className={styles.hintButton}
                            aria-label="Hint button"
                            onClick={() =>
                              setNotificationHintVisible(
                                !notificationHintVisible,
                              )
                            }
                          >
                            How to enable browser notification
                            <IconInfo width={16} height={16} />
                          </button>
                        )}
                        <NavLink
                          to={AdministrationRoutes.SETTINGS_PROFILE}
                          className={styles.profileSettingsLink}
                          onClick={() => setProfileMenuVisible(false)}
                        >
                          <IconAlternativeSettings />
                          <div className={styles.profileSettingsLinkText}>
                            Settings
                          </div>
                        </NavLink>
                        <button
                          type="button"
                          className={styles.logoutButton}
                          onClick={onLogout}
                        >
                          <div className={styles.iconWrapper}>
                            <IconLogout width={18} height={18} />
                          </div>
                          <div className={styles.textWrapper}>
                            {logoutInProgress ? 'Loading...' : 'Log out'}
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                </ClickOutsideDetector>

                {(activePermissions?.includes(PermissionEnum.MESSAGES_LIST) ||
                  activePermissions?.includes(PermissionEnum.CHANNELS_LIST) ||
                  activePermissions?.includes(
                    PermissionEnum.CHANNELS_CREATE_ACTION,
                  ) ||
                  activePermissions?.includes(
                    PermissionEnum.CHANNELS_UPDATE_ACTION,
                  )) && (
                  <button
                    type="button"
                    className={styles.notificationButton}
                    aria-label="Notification Button"
                    onClick={() => setNotificationPanelVisible(true)}
                  >
                    <IconNotification />
                    {notificationsMessages?.find((item) => !item.readAt) && (
                      <div className={styles.unreadHint} />
                    )}
                  </button>
                )}
              </>
            </HeaderRightSidebar>
          </Header>
        </div>
        <SidePanel
          type={SidePanelType.Customers}
          closeOnClickOutside
          isOpen={notificationPanelVisible}
          onClose={() => setNotificationPanelVisible(false)}
          className={styles.notificationSidepanel}
          title={
            <div className={styles.notificationsSidepanelTitle}>
              Notifications
              {isFetching && (
                <span>
                  (Updating... <IconLoader width={14} height={14} />)
                </span>
              )}
            </div>
          }
          sidepanelExpandedKey="notification-panel"
          footer={
            <>
              <ButtonSecondary
                text={
                  isLoading && !onReadInProgress
                    ? 'Loading...'
                    : 'Mark all as read'
                }
                className={styles.markReadButton}
                onClick={onReadAll}
                bold
                disabled={!notificationsMessages?.find((item) => !item.readAt)}
              />
              <ButtonPseudo
                text="Close"
                onClick={() => setNotificationPanelVisible(false)}
                bold
              />
            </>
          }
        >
          {notificationMessagesContent}
        </SidePanel>
        <div className={styles.layoutContent}>
          <div className={styles.layoutLeft}>
            <Sidebar>
              {activePermissions.includes(PermissionEnum.SECTION_DASHBOARD) ? (
                <>
                  <SidebarLink
                    label="Dashboard"
                    to={DashboardRoutes.DASHBOARD}
                    onMouseDown={(e) => {
                      if (e.button === 0) {
                        removeStorageFilters();
                        apiAbortController.abortPendingRequest();
                      }
                    }}
                  />
                  <div className={styles.sidebarSeparator} />
                </>
              ) : (
                <div />
              )}
              <SidebarItem
                hidden={
                  !activePermissions.includes(PermissionEnum.SECTION_ACCOUNTS)
                }
                links={[
                  {
                    label: 'Accounts list',
                    to: AccountsRoutes.ACCOUNTS_LIST,
                    hidden: !activePermissions.includes(
                      PermissionEnum.ACCOUNTS_LIST,
                    ),
                  },
                  {
                    label: 'External Integrations',
                    to: AccountsRoutes.EXTERNAL_ACCOUNTS,
                    hidden: Boolean(
                      !activePermissions.includes(
                        PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
                      ) &&
                      !activePermissions.includes(
                        PermissionEnum.EXTERNAL_ORGANIZATIONS_LIST,
                      ),
                    ),
                  },
                  {
                    label: 'Statement of Accounts',
                    to: AccountsRoutes.STATEMENTS_OF_ACCOUNTS,
                    hidden: !activePermissions.includes(
                      PermissionEnum.ACCOUNTS_STATEMENTS,
                    ),
                  },
                  {
                    label: 'Chart of Accounts',
                    to: AccountsRoutes.CHARTS_OF_ACCOUNTS,
                    hidden: !activePermissions.includes(
                      PermissionEnum.ACCOUNTS_CHART,
                    ),
                  },
                ]}
                location={AccountsRoutes.ACCOUNTS}
                pathname={pathname}
                onMouseDown={(e) => {
                  if (e.button === 0) {
                    removeStorageFilters();
                    apiAbortController.abortPendingRequest();
                  }
                }}
              >
                Accounts
              </SidebarItem>
              <SidebarItem
                hidden={
                  !activePermissions.includes(PermissionEnum.SECTION_CUSTOMERS)
                }
                links={[
                  {
                    label: 'Individual Customers',
                    to: ClientsRoutes.CUSTOMERS_INDIVIDUAL,
                    hidden: !activePermissions.includes(
                      PermissionEnum.INDIVIDUAL_CUSTOMERS_LIST,
                    ),
                  },
                  {
                    label: 'Corporate Customers',
                    to: ClientsRoutes.CUSTOMERS_CORPORATE,
                    hidden: !activePermissions.includes(
                      PermissionEnum.CORPORATE_CUSTOMERS_LIST,
                    ),
                  },
                  {
                    label: 'Messages',
                    to: MessagesRoutes.MESSAGES,
                    hidden: !activePermissions.includes(
                      PermissionEnum.MESSAGES_LIST,
                    ),
                  },
                  {
                    label: 'Applications',
                    to: ClientsRoutes.CUSTOMERS_NEW_APPLICATION,
                    hidden: !activePermissions.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_LIST,
                    ),
                  },
                  {
                    label: 'Notification center',
                    to: ClientsRoutes.CUSTOMERS_NOTIFICATIONS_CENTER,
                    hidden: !activePermissions.includes(
                      PermissionEnum.NOTIFICATIONS_MESSAGING_AND_CUSTOMERS_LIST,
                    ),
                  },
                ]}
                locations={[ClientsRoutes.CUSTOMERS, MessagesRoutes.MESSAGES]}
                pathname={pathname}
                onMouseDown={(e) => {
                  if (e.button === 0) {
                    removeStorageFilters();
                    apiAbortController.abortPendingRequest();
                  }
                }}
              >
                Customers
              </SidebarItem>
              <div>
                {(activePermissions.includes(PermissionEnum.SECTION_AML_KYC) ||
                  activePermissions.includes(
                    PermissionEnum.SECTION_TRANSACTIONS,
                  ) ||
                  activePermissions.includes(
                    PermissionEnum.SECTION_REPORTS,
                  )) && <div className={styles.sidebarSeparator} />}
              </div>
              <SidebarItem
                hidden={
                  !activePermissions.includes(PermissionEnum.SECTION_AML_KYC)
                }
                links={[
                  {
                    label: 'Block List',
                    to: AmlKycRoutes.BLACKLIST,
                    hidden: !activePermissions.includes(
                      PermissionEnum.BLACKLISTS_LIST,
                    ),
                  },
                  {
                    label: 'Geographical risk',
                    to: AmlKycRoutes.GEOGRAPHICAL,
                    hidden: !activePermissions.includes(
                      PermissionEnum.AML_GEO_LIST,
                    ),
                  },
                  {
                    label: 'Transactions risk',
                    to: AmlKycRoutes.TRANSACTIONS_RISK,
                    hidden: !activePermissions.includes(
                      PermissionEnum.SETTINGS_TRANSACTIONS_VOLUMES_LIST,
                    ),
                  },
                  {
                    label: 'Individual clients risk',
                    to: AmlKycRoutes.INDIVIDUAL_CLIENTS_RISK,
                  },
                  {
                    label: 'Corporate clients risk',
                    to: AmlKycRoutes.CORPORATE_CLIENTS_RISK,
                  },
                  {
                    label: 'Transaction monitoring rules',
                    to: AmlKycRoutes.TRANSACTION_MONITORING_RULES,
                    hidden: !activePermissions.includes(
                      PermissionEnum.AML_MONITORING_RULES_LIST,
                    ),
                  },
                  {
                    label: 'Risk levels',
                    to: AmlKycRoutes.RISK_LEVELS,
                    hidden: !activePermissions.includes(
                      PermissionEnum.SETTINGS_RISK_SCORE_RANGES_LIST,
                    ),
                  },
                ]}
                locations={[
                  AmlKycRoutes.BLACKLIST,
                  AmlKycRoutes.GEOGRAPHICAL,
                  AmlKycRoutes.TRANSACTIONS_RISK,
                  AmlKycRoutes.INDIVIDUAL_CLIENTS_RISK,
                  AmlKycRoutes.CORPORATE_CLIENTS_RISK,
                  AmlKycRoutes.TRANSACTION_MONITORING_RULES,
                  AmlKycRoutes.RISK_LEVELS,
                ]}
                pathname={pathname}
                onMouseDown={(e) => {
                  if (e.button === 0) {
                    removeStorageFilters();
                    apiAbortController.abortPendingRequest();
                  }
                }}
              >
                AML & KYC
              </SidebarItem>
              <SidebarItem
                hidden={
                  !activePermissions.includes(
                    PermissionEnum.SECTION_TRANSACTIONS,
                  )
                }
                links={[
                  {
                    label: 'Import transactions',
                    to: TransactionsRoutes.IMPORT,
                    hidden: !activePermissions.includes(
                      PermissionEnum.TRANSACTIONS_IMPORT_LIST,
                    ),
                  },
                  {
                    label: 'Export transactions',
                    to: TransactionsRoutes.EXPORT,
                    hidden: !activePermissions.includes(
                      PermissionEnum.TRANSACTIONS_EXPORT_LIST,
                    ),
                  },
                  {
                    label: 'Incoming transactions',
                    to: TransactionsRoutes.INCOMING,
                    hidden:
                      !activePermissions.includes(
                        PermissionEnum.TRANSACTIONS_INCOMING_LIST,
                      ) ||
                      !transactionsTypes?.find(
                        (item) => item.name === 'INCOMING',
                      )?.active,
                  },
                  {
                    label: 'Cash collection',
                    to: TransactionsRoutes.CASH_COLLECTION,
                    hidden:
                      !activePermissions.includes(
                        PermissionEnum.TRANSACTIONS_INCOMING_LIST,
                      ) || !cashCollectionEnabledValue,
                  },
                  {
                    label: 'Outgoing transactions',
                    to: TransactionsRoutes.OUTGOING,
                    hidden:
                      !activePermissions.includes(
                        PermissionEnum.TRANSACTIONS_OUTGOING_LIST,
                      ) ||
                      !transactionsTypes?.find(
                        (item) => item.name === 'OUTGOING',
                      )?.active,
                  },
                  {
                    label: 'Internal transfer',
                    to: TransactionsRoutes.INTERNAL_TRANSACTION,
                    hidden:
                      !activePermissions.includes(
                        PermissionEnum.TRANSACTIONS_INTERNAL_LIST,
                      ) ||
                      !transactionsTypes?.find(
                        (item) => item.name === 'INTERNAL',
                      )?.active,
                  },
                  {
                    label: 'Exchange',
                    to: TransactionsRoutes.EXCHANGE,
                    hidden:
                      !activePermissions.includes(
                        PermissionEnum.TRANSACTIONS_EXCHANGE_LIST,
                      ) ||
                      !transactionsTypes?.find(
                        (item) => item.name === 'CURRENCY_EXCHANGE',
                      )?.active,
                  },
                  {
                    label: 'General Ledger transactions',
                    to: TransactionsRoutes.GENERAL_LEDGER,
                    hidden:
                      !activePermissions.includes(
                        PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_LIST,
                      ) ||
                      !transactionsTypes?.find(
                        (item) => item.name === 'GENERAL_LEDGER',
                      )?.active,
                  },
                  {
                    label: 'Nostro transfer',
                    to: TransactionsRoutes.NOSTRO_TRANSFERS,
                    hidden:
                      !activePermissions.includes(
                        PermissionEnum.TRANSACTIONS_NOSTRO_LIST,
                      ) ||
                      !transactionsTypes?.find((item) => item.name === 'NOSTRO')
                        ?.active,
                  },
                  {
                    label: 'Notification center',
                    to: TransactionsRoutes.TRANSACTIONS_NOTIFICATIONS_CENTER,
                    hidden: !activePermissions.includes(
                      PermissionEnum.NOTIFICATIONS_TRANSACTIONS_LIST,
                    ),
                  },
                ]}
                pathname={pathname}
                location={TransactionsRoutes.TRANSACTIONS}
                onMouseDown={(e) => {
                  if (e.button === 0) {
                    removeStorageFilters();
                    apiAbortController.abortPendingRequest();
                  }
                }}
              >
                Transactions
              </SidebarItem>
              {activePermissions.includes(PermissionEnum.SECTION_CARDS) &&
              cardsEnabled ? (
                <SidebarLink
                  label="Cards"
                  to={CardsRoutes.CARDS}
                  onMouseDown={(e) => {
                    if (e.button === 0) {
                      removeStorageFilters();
                      apiAbortController.abortPendingRequest();
                    }
                  }}
                />
              ) : (
                <div />
              )}
              <SidebarItem
                hidden={
                  !activePermissions.includes(PermissionEnum.SECTION_REPORTS)
                }
                links={[
                  {
                    label: 'Balance sheet',
                    to: ReportsRoutes.REPORTS_BALANCE,
                    hidden: !activePermissions.includes(
                      PermissionEnum.REPORTS_BALANCE_SHEET_LIST,
                    ),
                  },
                  {
                    label: 'Profit-Loss',
                    to: ReportsRoutes.REPORTS_PROFIT_lOSS,
                    hidden: !activePermissions.includes(
                      PermissionEnum.REPORTS_PROFIT_LOSS_LIST,
                    ),
                  },
                  {
                    label: 'Safeguarding report',
                    to: ReportsRoutes.REPORTS_SAFEGUARDING,
                    hidden: !activePermissions.includes(
                      PermissionEnum.REPORTS_SAFEGUARDING_LIST,
                    ),
                  },
                  {
                    label: 'Capital adequacy report',
                    to: ReportsRoutes.REPORTS_CAPITAL_ADEQUACY,
                    hidden: !activePermissions.includes(
                      PermissionEnum.REPORTS_CAPITAL_ADEQUACY_LIST,
                    ),
                  },
                  {
                    label: 'Banks report',
                    to: ReportsRoutes.REPORTS_BANK_AND_PSP,
                    hidden: !activePermissions.includes(
                      PermissionEnum.REPORTS_PSP_LIST,
                    ),
                  },
                  {
                    label: 'Currency position report',
                    to: ReportsRoutes.REPORTS_CURRENCY_POSITION,
                    hidden: !activePermissions.includes(
                      PermissionEnum.REPORTS_CURRENCY_POSITION_LIST,
                    ),
                  },
                  {
                    label: 'Revaluation report',
                    to: ReportsRoutes.REPORTS_REVALUATION,
                    hidden: !activePermissions.includes(
                      PermissionEnum.REPORTS_REVALUATION_LIST,
                    ),
                  },
                  {
                    label: 'Transaction Status Report',
                    to: ReportsRoutes.TRANSACTION_STATUS_REPORT,
                  },
                  {
                    label: 'Warnings Report',
                    to: ReportsRoutes.WARNING_REPORT,
                    hidden: !activePermissions?.includes(
                      PermissionEnum.WARNINGS_LIST,
                    ),
                  },
                  {
                    label: 'External reports (Fintrac)',
                    to: ReportsRoutes.FINTRAC,
                    hidden: !activePermissions?.includes(
                      PermissionEnum.REGULATORY_REPORT_FINTRAC_LIST,
                    ),
                  },
                  {
                    label: 'Accounting management',
                    to: ReportsRoutes.REPORTS_ACCOUNTING_MANAGMENT,
                    hidden: !activePermissions?.includes(
                      PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS,
                    ),
                  },
                  {
                    label: 'Notification center',
                    to: ReportsRoutes.REPORTS_NOTIFICATIONS_CENTER,
                    hidden: true,
                  },
                ]}
                pathname={pathname}
                location={ReportsRoutes.REPORTS}
                onMouseDown={(e) => {
                  if (e.button === 0) {
                    removeStorageFilters();
                    apiAbortController.abortPendingRequest();
                  }
                }}
              >
                Reports
              </SidebarItem>
              <div>
                {(activePermissions.includes(PermissionEnum.SECTION_REPORTS) ||
                  activePermissions.includes(PermissionEnum.SECTION_CARDS) ||
                  activePermissions.includes(
                    PermissionEnum.SECTION_TRANSACTIONS,
                  ) ||
                  activePermissions.includes(
                    PermissionEnum.SECTION_AML_KYC,
                  )) && <div className={styles.sidebarSeparator} />}
              </div>
              <SidebarItem
                hidden={!activePermissions.includes(PermissionEnum.SECTION_PSP)}
                links={[
                  {
                    label: 'Banks',
                    to: BankRoutes.BANK_AND_PSP,
                    hidden: !activePermissions.includes(
                      PermissionEnum.PSP_LIST,
                    ),
                  },
                  {
                    label: 'Integration logs',
                    to: BankRoutes.INTEGRATIONS_LOGS,
                    hidden: !activePermissions.includes(
                      PermissionEnum.PSP_INTEGRATION_LOGS_LIST,
                    ),
                  },
                ]}
                locations={[
                  BankRoutes.BANK_AND_PSP,
                  BankRoutes.INTEGRATIONS_LOGS,
                ]}
                pathname={pathname}
                onMouseDown={(e) => {
                  if (e.button === 0) {
                    removeStorageFilters();
                    apiAbortController.abortPendingRequest();
                  }
                }}
              >
                Banks
              </SidebarItem>
              <SidebarItem
                hidden={
                  !activePermissions.includes(
                    PermissionEnum.SECTION_EXCHANGE_SETTINGS,
                  )
                }
                links={[
                  {
                    label: 'Currencies',
                    to: CurrenciesRoutes.CURRENCIES,
                    hidden: !activePermissions.includes(
                      PermissionEnum.SETTINGS_CURRENCIES_LIST,
                    ),
                  },
                  {
                    label: 'Rates',
                    to: ExchangeRoutes.RATES,
                    hidden: !activePermissions.includes(
                      PermissionEnum.SETTINGS_EXCHANGE_RATES_LIST,
                    ),
                  },
                  {
                    label: 'Margins',
                    to: ExchangeRoutes.MARGINS,
                    hidden: !activePermissions.includes(
                      PermissionEnum.SETTINGS_EXCHANGE_MARGINS_LIST,
                    ),
                  },
                  {
                    label: 'Auto FX settings',
                    to: ExchangeRoutes.AUTO_SETTINGS,
                    hidden: !activePermissions.includes(
                      PermissionEnum.SETTINGS_AUTO_EXCHANGE_LIST,
                    ),
                  },
                  {
                    label: 'FX Rate request',
                    to: ExchangeRoutes.RATE_REQUEST,
                    hidden:
                      !activePermissions.includes(
                        PermissionEnum.SETTINGS_CUSTOM_RATE_EXCHANGE_LIST,
                      ) ||
                      !settings?.find(
                        (item) => item.key === 'EXCHANGE-CUSTOM-RATE-ENABLED',
                      )?.data.value,
                  },
                ]}
                pathname={pathname}
                locations={[
                  CurrenciesRoutes.CURRENCIES,
                  ExchangeRoutes.EXCHANGE,
                ]}
                onMouseDown={(e) => {
                  if (e.button === 0) {
                    removeStorageFilters();
                    apiAbortController.abortPendingRequest();
                  }
                }}
              >
                Exchange
              </SidebarItem>
              <SidebarItem
                hidden={
                  !activePermissions.includes(
                    PermissionEnum.SECTION_FEE_TARIFFS,
                  )
                }
                links={[
                  {
                    label: 'Tariffs',
                    to: TariffsRoutes.TARIFFS,
                    hidden: !activePermissions.includes(
                      PermissionEnum.TARIFFS_LIST,
                    ),
                  },
                  {
                    label: 'Fee types',
                    to: FeesRoutes.FEES,
                    hidden: !activePermissions.includes(
                      PermissionEnum.FEES_TYPES_LIST,
                    ),
                  },
                ]}
                pathname={pathname}
                locations={[TariffsRoutes.TARIFFS, FeesRoutes.FEES]}
                onMouseDown={(e) => {
                  if (e.button === 0) {
                    removeStorageFilters();
                    apiAbortController.abortPendingRequest();
                  }
                }}
              >
                Fees & Tariffs
              </SidebarItem>
              <div>
                {(activePermissions.includes(
                  PermissionEnum.SECTION_ADMINISTRATION,
                ) ||
                  activePermissions?.includes(
                    PermissionEnum.NOTIFICATIONS_LIST,
                  )) && <div className={styles.sidebarSeparator} />}
              </div>
              <SidebarItem
                links={[
                  {
                    label: 'Notification center',
                    to: NotificationCenterRoutes.NOTIFICATIONS_CENTER,
                  },
                  {
                    label: 'Notifications',
                    to: NotificationCenterRoutes.NOTIFICATIONS_SETTINGS,
                    hidden: true,
                  },
                ]}
                pathname={pathname}
                location={NotificationCenterRoutes.NOTIFICATIONS}
                onMouseDown={(e) => {
                  if (e.button === 0) {
                    removeStorageFilters();
                    apiAbortController.abortPendingRequest();
                  }
                }}
                hidden={
                  !activePermissions.includes(
                    PermissionEnum.SECTION_NOTIFICATIONS,
                  )
                }
              >
                Notifications
              </SidebarItem>
              <SidebarItem
                hidden={
                  !activePermissions.includes(
                    PermissionEnum.SECTION_ADMINISTRATION,
                  )
                }
                links={[
                  {
                    label: 'Employees list',
                    to: AdministrationRoutes.USER_LIST,
                    hidden: !activePermissions.includes(
                      PermissionEnum.USERS_LIST,
                    ),
                  },
                  {
                    label: 'Employees Roles',
                    to: AdministrationRoutes.USER_ROLES,
                    hidden: !activePermissions.includes(
                      PermissionEnum.USERS_ROLES_LIST,
                    ),
                  },
                  {
                    label: 'Users list',
                    to: AdministrationRoutes.CLIENTS_LIST,
                    hidden: !activePermissions.includes(
                      PermissionEnum.SECTION_CUSTOMERS,
                    ),
                  },
                  {
                    label: 'Change log',
                    to: AdministrationRoutes.CHANGE_LOGS,
                    hidden: !activePermissions.includes(
                      PermissionEnum.AUDIT_LOG_LIST,
                    ),
                  },
                  {
                    label: 'Notification settings',
                    to: AdministrationRoutes.NOTIFICATION_SETTINGS,
                    hidden: !activePermissions.includes(
                      PermissionEnum.NOTIFICATIONS_TYPES_TEMPLATE_LIST,
                    ),
                  },
                  {
                    label: 'Security',
                    to: AdministrationRoutes.SECURITY,
                    hidden: Boolean(
                      !activePermissions.includes(PermissionEnum.LOCKS_LIST) &&
                      !activePermissions.includes(
                        PermissionEnum.AUDIT_LOG_LIST,
                      ),
                    ),
                  },
                  {
                    label: 'Anonymisation',
                    to: AdministrationRoutes.ANONYMISATION,
                    hidden: Boolean(
                      !activePermissions.includes(
                        PermissionEnum.ANONYMIZE_LOGS_LIST,
                      ),
                    ),
                  },
                ]}
                pathname={pathname}
                location={AdministrationRoutes.ADMINISTRATION}
                onMouseDown={(e) => {
                  if (e.button === 0) {
                    removeStorageFilters();
                    apiAbortController.abortPendingRequest();
                  }
                }}
              >
                Administration
              </SidebarItem>

              <div>
                {activePermissions.includes(
                  PermissionEnum.SECTION_ADMINISTRATION,
                ) && <div className={styles.sidebarSeparator} />}
              </div>
              <SidebarItem
                hidden={Boolean(
                  !activePermissions.includes(
                    PermissionEnum.SECTION_ADMINISTRATION,
                  ) ||
                  !activePermissions.includes(
                    PermissionEnum.SETTINGS_SYSTEM_LIST,
                  ),
                )}
                links={[
                  {
                    label: 'Currencies',
                    to: AdministrationRoutes.CURRENCIES,
                  },
                  {
                    label: 'Countries',
                    to: AdministrationRoutes.COUNTRIES,
                  },
                  {
                    label: 'PSP settings',
                    to: AdministrationRoutes.PSP,
                  },
                  {
                    label: 'Agreements',
                    to: AdministrationRoutes.AGREEMENTS_LIST,
                  },
                  {
                    label: 'Reports Settings',
                    to: AdministrationRoutes.REPORTS_SETTINGS,
                    hidden: !activePermissions?.includes(
                      PermissionEnum.SETTINGS_REGULATORY_REPORT_FINTRAC_EDIT_ACTION,
                    ),
                  },
                  {
                    label: 'Transaction settings',
                    to: AdministrationRoutes.TRANSACTION_SETTINGS,
                  },
                  {
                    label: 'Applications settings',
                    to: AdministrationRoutes.APPLICATIONS,
                    hidden: !activePermissions.includes(
                      PermissionEnum.APPLICATIONS_SCHEMES_LIST,
                    ),
                  },
                  {
                    label: 'Dictionaries',
                    to: AdministrationRoutes.DICTIONARIES,
                    hidden: !activePermissions.includes(
                      PermissionEnum.DICTIONARIES_LIST,
                    ),
                  },
                  {
                    label: 'Other',
                    to: AdministrationRoutes.OTHER_SETTINGS,
                  },
                ]}
                pathname={pathname}
                location={AdministrationRoutes.SETTINGS}
                onMouseDown={(e) => {
                  if (e.button === 0) {
                    removeStorageFilters();
                    apiAbortController.abortPendingRequest();
                  }
                }}
              >
                Settings
              </SidebarItem>
            </Sidebar>
          </div>
          <div className={styles.layoutRight} id="layout-right">
            {children}
          </div>
        </div>
        {notificationVisible?.isVisible && (
          <div className={styles.reportNotification}>
            <div className={styles.reportNotificationLeft}>
              {!notificationVisible?.loading ? (
                <IconSelected
                  width={18}
                  height={18}
                  className={styles.selectedIcon}
                />
              ) : (
                <IconLoader />
              )}
              <div className={styles.reportText}>{notificationText}</div>
            </div>
            <button
              type="button"
              className={styles.closeNotificationButton}
              onClick={() =>
                dispatch(
                  setNotificationVisible({
                    visible: false,
                    text: '',
                    type: '',
                  }),
                )
              }
            >
              Close
            </button>
          </div>
        )}

        <NotifiactionHintPopup
          popupVisible={notificationHintVisible}
          setPopupVisible={setNotificationHintVisible}
        />
        <NotifiactionDisabledPopup
          popupVisible={notificationDisabledPopupVisible}
          setPopupVisible={setNotificationDisabledPopupVisible}
        />
      </>
    );
  } else {
    content = (
      <div className={styles.layoutLoader}>
        <IconLoader />
      </div>
    );
  }

  return (
    <>
      {content}
      <NetworkStatusBanner />
    </>
  );
};

const LayoutWrapper = ({
  children,
  sendMessage,
  reSubscribe = false,
  setNotificationPanelVisible,
  notificationPanelVisible,
  selectedNotificationId,
  setSelectedNotificationId,
}: LayoutWrapperTypes) => {
  const [getMessagesToken] = useGetRootMessageTokenMutation();

  const dispatch = useAppDispatch();

  const {
    accessToken,
    activePermissions,
    userId,
    notificationChannelId,
    notificationsMessages,
  } = useAppSelector((state) => state.root);

  const { accessToken: messagesAccessToken } = useAppSelector(
    (state) => state.messages.general,
  );
  const { accessToken: transactionsAccessToken } = useAppSelector(
    (state) => state.transactions,
  );

  const [getNotifications] = useLazyGetNotificationChannelsQuery();
  const [getNotificationsMessages, { isLoading: messagesIsLoading }] =
    useLazyGetNotificationChannelMessagesQuery();

  const getNotificationsAction = useCallback(
    async (id: string) => {
      const response = await getNotifications(id);

      if (
        response &&
        response.data &&
        response.data.data &&
        response.data.data.edges.length &&
        !notificationsMessages
      ) {
        const channel = response.data.data.edges[0].node;

        if (channel) {
          dispatch(setNotificationChannelId(channel.id));

          getNotificationsMessages({ id: channel.id });
        }
      }
    },
    [
      dispatch,
      getNotifications,
      getNotificationsMessages,
      notificationsMessages,
    ],
  );

  useEffect(() => {
    if (notificationChannelId) {
      sendMessage(
        JSON.stringify({
          event: 'subscribe',
          data: { channelId: notificationChannelId },
        }),
      );
    }
  }, [notificationChannelId, sendMessage]);

  useEffect(() => {
    if (accessToken) {
      if (!messagesAccessToken) {
        dispatch(setMessagesAccessToken(accessToken));
      }

      if (!transactionsAccessToken) {
        dispatch(setTransactionsAccessToken(accessToken));
      }
    }
  }, [accessToken, messagesAccessToken, transactionsAccessToken, dispatch]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.CHANNELS_LIST) &&
      userId &&
      accessToken
    ) {
      getNotificationsAction(userId);
    }
  }, [activePermissions, userId, accessToken, getNotificationsAction]);

  useEffect(() => {
    if (
      !accessToken &&
      (activePermissions?.includes(PermissionEnum.MESSAGES_LIST) ||
        activePermissions?.includes(PermissionEnum.CHANNELS_LIST) ||
        activePermissions?.includes(PermissionEnum.CHANNELS_CREATE_ACTION) ||
        activePermissions?.includes(PermissionEnum.CHANNELS_UPDATE_ACTION))
    ) {
      getMessagesToken();
    }
  }, [accessToken, activePermissions, getMessagesToken]);

  let content;

  if (
    !accessToken &&
    (activePermissions?.includes(PermissionEnum.MESSAGES_LIST) ||
      activePermissions?.includes(PermissionEnum.CHANNELS_LIST) ||
      activePermissions?.includes(PermissionEnum.CHANNELS_CREATE_ACTION) ||
      activePermissions?.includes(PermissionEnum.CHANNELS_UPDATE_ACTION))
  ) {
    content = (
      <div className={styles.layoutLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Layout
        sendMessage={sendMessage}
        messagesIsLoading={messagesIsLoading}
        reSubscribe={reSubscribe}
        setNotificationPanelVisible={setNotificationPanelVisible}
        notificationPanelVisible={notificationPanelVisible}
        selectedNotificationId={selectedNotificationId}
        setSelectedNotificationId={setSelectedNotificationId}
      >
        {children}
      </Layout>
    );
  }

  const toMatch = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
  ];

  const isMobile = toMatch.some((toMatchItem) =>
    navigator.userAgent.match(toMatchItem),
  );

  return (
    <ActiveTabCtxProvider>
      <div
        className={`${styles.layout} ${!isMobile ? styles.layoutDesktop : ''}`}
      >
        {content}
      </div>
    </ActiveTabCtxProvider>
  );
};

export default LayoutWrapper;
